import React, { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Paginator } from "primereact/paginator";
import { Column } from "primereact/column";
import { Link } from "react-router-dom";
import { EditOutlined, DeleteOutlined } from "@material-ui/icons";
import DeleteConfirmModal from "./DeleteConfirmModal";

const BTN_TABLE_STYLE = {
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	width: "32px",
	height: "32px",
	padding: "8px",
	background: "var(--gray-3)",
	borderRadius: "2px",
	border: "none",
	cursor: "pointer",
	color: "var(--gray-9)",
	textDecoration: "none",
	flexShrink: 0,
	boxSizing: "border-box",
};

export default function BetterDataTable({ refresh, setRefresh, fetchEvent, className, noPaginator, scrollable, children, opcoesCrud, crudUrl, noShow, noEdit, noDelete, deleteHandler, customOptions }) {
	const [data, setData] = useState([]);
	const [rows, setRows] = useState(15);
	const [totalRecords, setTotalRecords] = useState(0);
	const [first, setFirst] = useState(0);
	const [last, setLast] = useState(0);
	const [loading, setLoading] = useState(false);
	const [globalFilter, setGlobalFilter] = useState(null);
	const [sortOrder, setSortOrder] = useState(null);
	const [sortField, setSortField] = useState(null);
	const [showDelete, setShowDelete] = useState(false);
	const [idItem, setIdItem] = useState(null);

	const dt = useRef(null);

	useEffect(() => {
		if (refresh) {
			setRefresh(false);
			refreshTable();
		}
	}, [refresh]);

	useEffect(() => {
		onPage({ rows, first, last, globalFilter });
	}, []);

	const doGlobalFiltering = (value) => {
		setGlobalFilter(value);

		setTimeout(() => {
			onPage({ rows, first, last, globalFilter: value });
		}, 350);
	};

	const refreshTable = () => {
		onPage({ rows, first, last, globalFilter });
	};

	const onPage = (event) => {
		setLoading(true);

		const effectiveRows = event.rows ?? rows;
		const effectiveSortField = event.sortField ?? sortField;
		const effectiveSortOrder = event.sortOrder ?? sortOrder;

		fetchEvent({
			...event,
			rows: effectiveRows,
			sortField: effectiveSortField,
			sortOrder: effectiveSortOrder,
		})
			.then((res) => {
				const result = res.data.data;
				setData(result.data);
				setTotalRecords(result.total);
				setFirst(result.from - 1);
				setLast(result.to - 1);
				setLoading(false);
			});
	};

	const sortTable = (e) => {
		setSortField(e.sortField);
		setSortOrder(e.sortOrder);
		onPage({ ...e, rows });
	};

	const handleDelete = () => {
		if (!deleteHandler || !idItem) {
			setShowDelete(false);
			setIdItem(null);

			return;
		}

		setLoading(true);

		deleteHandler(idItem)
			.then(() => {
				refreshTable();
				setShowDelete(false);
				setIdItem(null);
			})
			.catch((error) => {
				console.error("Erro ao excluir:", error);
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const renderBotoesCrud = (id) => (
		<div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
			{typeof customOptions === "function" ? customOptions(id) : null}

			{!noShow && (
				<Link to={`${crudUrl}/show/${id}`} style={BTN_TABLE_STYLE}>
					<span className="material-symbols-outlined" style={{ fontSize: "16px" }}>visibility</span>
				</Link>
			)}

			{!noEdit && (
				<Link to={`${crudUrl}/edit/${id}`} style={BTN_TABLE_STYLE}>
					<EditOutlined style={{ fontSize: "16px" }} />
				</Link>
			)}

			{!noDelete && (
				<button
					style={BTN_TABLE_STYLE}
					onClick={() => {
						setIdItem(id);
						setShowDelete(true);
					}}
				>
					<DeleteOutlined style={{ fontSize: "16px" }} />
				</button>
			)}
		</div>
	);

	return (
		<div className={`custom-table ${className ?? ""}`}>
			<DataTable
				className="desk"
				ref={dt}
				value={data}
				dataKey="id"
				sortField={sortField}
				sortOrder={sortOrder}
				sortMode="single"
				paginator={!noPaginator && !(data?.length === 0)}
				rows={rows}
				csvSeparator=";"
				first={first}
				last={last}
				globalFilter={globalFilter}
				responsive={!scrollable}
				scrollable={scrollable}
				totalRecords={totalRecords}
				lazy
				onPage={onPage}
				loading={loading}
				loadingIcon="fas fa-sync fa-spin"
				paginatorTemplate="CurrentPageReport PrevPageLink PageLinks NextPageLink"
				currentPageReportTemplate={`Mostrando ${data?.length} de {totalRecords} resultados`}
				emptyMessage="Nenhum registro encontrado!"
				onSort={sortTable}
			>
				{children}

				{opcoesCrud && crudUrl && (
					<Column
						header="Ações"
						style={{ width: "120px" }}
						body={(rowData) => renderBotoesCrud(rowData.id)}
					/>
				)}
			</DataTable>

			<div className="datatable-mobile">
				{data?.map((row, index) => {
					const childrenArray = React.Children.toArray(children);
					let titleElement = null;
					let subTitleElement = null;
					let otherFields = [];
					let titleColumnIndex = -1;
					const explicitTitleFound = childrenArray.some(col => col.props.mobileTitle);

					childrenArray.forEach((col, i) => {
						if (col.props.mobileHidden) {
							return;
						}

						const field = col.props.field;
						const header = col.props.header;
						const body = col.props.body;
						const value = body ? body(row) : row[field];
						const isTitle = col.props.mobileTitle;
						const isSubTitle = col.props.mobileSubTitle;

						if (isTitle || (!explicitTitleFound && i === 0)) {
							if (!titleElement) {
								titleElement = (
									<div key={`title-${i}`} className="datatable-mobile-title">
										<span className="dt-title ellipsis">{value}</span>
										{opcoesCrud && crudUrl && renderBotoesCrud(row.id)}
									</div>
								);
								titleColumnIndex = i;
								return;
							}
						}

						if (isSubTitle) {
							if (!subTitleElement) {
								subTitleElement = (
									<div key={`subtitle-${i}`} className="datatable-mobile-subtitle">
										{value}
									</div>
								);
								return;
							}
						}

						if (i !== titleColumnIndex && !isSubTitle) {
							otherFields.push(
								<div key={`field-${i}`} className="datatable-mobile-field">
									<span className="head">{header}</span>{" "}
									<span className="valor">{value}</span>
								</div>
							);
						}
					});

					return (
						<div key={index} className="datatable-mobile-card">
							{titleElement}
							{subTitleElement}
							{otherFields}
						</div>
					);
				})}

				{!noPaginator && !(data?.length === 0) && (
					<Paginator
						first={first}
						rows={rows}
						totalRecords={totalRecords}
						onPageChange={onPage}
						template="CurrentPageReport PrevPageLink PageLinks NextPageLink"
						currentPageReportTemplate={`Mostrando ${data?.length} de {totalRecords} resultados`}
					/>
				)}
			</div>

			<DeleteConfirmModal
				open={showDelete}
				onClose={() => {
					setShowDelete(false);
					setIdItem(null);
				}}
				onConfirm={handleDelete}
				loading={loading}
				message="Deseja remover este registro?"
			/>
		</div>
	);
}
